import {
  compareRob,
  isDecimalText,
  type RobComparison,
  type SoundingLineResult,
  soundingLineResult,
} from '@felix/domain';
import type { TDocumentDefinitions } from 'pdfmake/interfaces.js';
import { TOKENS } from '../../tokens.js';
import type { ReportTable } from '../reports/reports.js';
import type {
  FuelAnalysisData,
  FuelAnalysisRow,
  FuelComparison,
  FuelOverviewReconciliation,
  FuelSounding,
  FuelThresholds,
} from './types.js';

/**
 * ROB Mutabakatı ve Voyage Analizi ekranlarının saf yardımcıları (ADR 0117 Ek Y4): formun girdi
 * dönüşümü, satır ve karşılaştırma önizlemesi (domain'in aynı fonksiyonlarıyla; sunucu kayıtta
 * yeniden hesaplar), düzeltme satırları, Excel tablosu ve PDF tanımları.
 */

export type SoundingMethodValue = 'sounding' | 'ullage' | 'sensor' | 'manual';

export interface SoundingLineValues {
  tankId: string;
  method: SoundingMethodValue;
  readingCm: string;
  tempC: string;
  observedVolumeM3: string;
  density15: string;
  massMt: string;
}

export interface SoundingValues {
  day: string | null;
  time: string | null;
  sourceKind: string | null;
  sourceReference: string;
  measuredById: string | null;
  basis: string;
  lines: SoundingLineValues[];
}

const text = (value: string) => {
  const trimmed = value.trim().replace(',', '.');
  return trimmed === '' ? null : trimmed;
};
const note = (value: string) => {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

/** Satır dönüşüm dayanağı ister mi: elle MT ya da tablosuz sounding/ullage. */
export function needsBasis(line: SoundingLineValues, calibratedTankIds: readonly string[]) {
  return (
    line.method === 'manual' ||
    ((line.method === 'sounding' || line.method === 'ullage') &&
      !calibratedTankIds.includes(line.tankId))
  );
}

/** Formdan ölçüm girdisi (domain `fuelSoundingSchema`); dayanak gereken satırlara yazılır. */
export function soundingInputOf(
  values: SoundingValues,
  context: { calibratedTankIds: readonly string[] },
): Record<string, unknown> {
  return {
    measuredLocal: values.day && values.time ? `${values.day}T${values.time.slice(0, 5)}` : null,
    sourceKind: values.sourceKind,
    sourceReference: note(values.sourceReference),
    measuredById: values.measuredById,
    remarks: null,
    lines: values.lines.map((line) => ({
      tankId: line.tankId,
      method: line.method,
      readingCm: line.method === 'manual' ? null : text(line.readingCm),
      tempC: line.method === 'manual' ? null : text(line.tempC),
      observedVolumeM3: line.method === 'manual' ? null : text(line.observedVolumeM3),
      density15: line.method === 'manual' ? null : text(line.density15),
      massMt: line.method === 'manual' ? text(line.massMt) : null,
      basis: needsBasis(line, context.calibratedTankIds) ? note(values.basis) : null,
    })),
  };
}

/** Satırın MT önizlemesi (VCF 54B); eksik ya da geçersiz alanda `null`. */
export function previewLine(line: SoundingLineValues): SoundingLineResult | null {
  const pick = (value: string) => {
    const v = text(value);
    return v !== null && isDecimalText(v) ? v : null;
  };
  try {
    if (line.method === 'manual') {
      const mass = pick(line.massMt);
      return mass === null
        ? null
        : soundingLineResult(
            {
              method: 'manual',
              massMt: mass,
              observedVolumeM3: null,
              tempC: null,
              density15: null,
              basis: null,
            },
            { calibrationVersionId: null },
          );
    }
    const volume = pick(line.observedVolumeM3);
    const temp = pick(line.tempC);
    const density = pick(line.density15);
    if (volume === null || temp === null || density === null) return null;
    return soundingLineResult(
      {
        method: line.method,
        observedVolumeM3: volume,
        tempC: temp,
        density15: density,
        massMt: null,
        basis: null,
      },
      { calibrationVersionId: null },
    );
  } catch {
    return null;
  }
}

/** Kayıt öncesi karşılaştırma önizlemesi: güncel defter ROB'una göre; ölçülen satır yoksa `null`. */
export function previewComparison(
  values: SoundingValues,
  context: {
    tanks: readonly { id: string; fuelTypeId: string | null; robMt: string }[];
    thresholds: FuelThresholds;
  },
): RobComparison | null {
  const lines = values.lines
    .map((line) => ({ line, result: previewLine(line) }))
    .filter((x): x is { line: SoundingLineValues; result: SoundingLineResult } => x.result !== null)
    .map(({ line, result }) => ({
      tankId: line.tankId,
      method: line.method,
      measuredMt: result.massMt,
    }));
  if (lines.length === 0) return null;
  return compareRob({
    lines,
    calculatedByTank: Object.fromEntries(context.tanks.map((t) => [t.id, t.robMt])),
    fuelOfTank: Object.fromEntries(context.tanks.map((t) => [t.id, t.fuelTypeId])),
    thresholds: context.thresholds,
  });
}

/** Düzeltme talebinin tank satırları: sıfırdan ya da (seçilirse) ölçüm farkından. */
export function adjustmentLinesOf(
  comparison: Pick<FuelComparison, 'tanks'> | Pick<RobComparison, 'tanks'>,
  useDifferences: boolean,
): { tankId: string; fuelTypeId: string | null; massMt: string }[] {
  return comparison.tanks.map((row) => ({
    tankId: row.tankId,
    fuelTypeId: row.fuelTypeId,
    massMt: useDifferences ? row.diffMt : '0',
  }));
}

/** Analiz tablosunun Excel biçimi: satırlar, yakıt sütunları (MT) ve toplam; sayı hücresi. */
export function analysisWorkbookTable(
  data: Pick<FuelAnalysisData, 'fuelTypes' | 'table'>,
  view: {
    period: (row: FuelAnalysisRow) => string;
    operation: (row: FuelAnalysisRow) => string;
    coverage: (row: FuelAnalysisRow) => string;
    labels: { period: string; operation: string; cp: string; total: string; totalRow: string };
  },
): ReportTable {
  const fuels = analysisFuels(data);
  const num = (value: string | undefined) => (value === undefined ? 0 : Number(value));
  return {
    report: 'fuel_voyage_analysis',
    columns: [
      { key: 'period', kind: 'text', label: view.labels.period },
      { key: 'operation', kind: 'text', label: view.labels.operation },
      { key: 'cp', kind: 'text', label: view.labels.cp },
      ...fuels.map((f) => ({ key: f.id, kind: 'number' as const, label: `${f.code} (MT)` })),
      { key: 'total', kind: 'number', label: view.labels.total },
    ],
    rows: [
      ...data.table.rows.map((row) => [
        view.period(row),
        view.operation(row),
        view.coverage(row),
        ...fuels.map((f) => num(row.byFuel[f.id])),
        Number(row.total),
      ]),
      [
        view.labels.totalRow,
        null,
        null,
        ...fuels.map((f) => num(data.table.totals.byFuel[f.id])),
        Number(data.table.totals.total),
      ],
    ],
  } as unknown as ReportTable;
}

/** Tablodaki yakıtlar (tüketimi olan), ana kayıt sırasıyla. */
export function analysisFuels(
  data: Pick<FuelAnalysisData, 'fuelTypes' | 'table'>,
): { id: string; code: string }[] {
  const used = new Set(Object.keys(data.table.totals.byFuel));
  const known = data.fuelTypes
    .filter((f) => used.has(f.id))
    .map((f) => ({ id: f.id, code: f.code }));
  for (const id of used) {
    if (!known.some((f) => f.id === id)) known.push({ id, code: id.slice(0, 8) });
  }
  return known;
}

const CELL_LAYOUT = {
  hLineWidth: (i: number) => (i === 1 ? 0.8 : 0.4),
  vLineWidth: () => 0,
  hLineColor: () => TOKENS.border,
  paddingLeft: () => 4,
  paddingRight: () => 4,
  paddingTop: () => 3,
  paddingBottom: () => 3,
  fillColor: (row: number) => (row === 0 ? TOKENS['table-head-bg'] : null),
};

/** Basit tablo PDF'i (yönetim raporu): başlık, bilgi satırları, tablo(lar), dipnot. A4 yatay. */
export function tablePdf(input: {
  title: string;
  info: readonly (readonly [string, string])[];
  tables: readonly {
    title?: string;
    head: readonly string[];
    rows: readonly (readonly string[])[];
    numeric: readonly number[];
  }[];
  footer: string;
  notes?: readonly string[];
}): TDocumentDefinitions {
  return {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [36, 40, 36, 48],
    info: { title: input.title, creator: 'Felixmate' },
    defaultStyle: { fontSize: 8.5, color: TOKENS['navy-900'] },
    footer: (current, total) => ({
      columns: [
        { text: input.footer, color: TOKENS['text-secondary'] },
        { text: `${current} / ${total}`, alignment: 'right', color: TOKENS['text-secondary'] },
      ],
      margin: [36, 12, 36, 0],
      fontSize: 7.5,
    }),
    content: [
      { text: input.title, fontSize: 15, bold: true, margin: [0, 0, 0, 8] },
      {
        table: {
          widths: [110, '*'],
          body: input.info.map(([label, value]) => [{ text: label, bold: true }, value]),
        },
        layout: 'noBorders',
        margin: [0, 0, 0, 10],
      },
      ...input.tables.flatMap((table) => [
        ...(table.title
          ? [
              {
                text: table.title,
                bold: true,
                margin: [0, 6, 0, 4] as [number, number, number, number],
              },
            ]
          : []),
        {
          table: {
            headerRows: 1,
            widths: table.head.map((_, i) => (i === 0 ? '*' : 'auto')),
            body: [
              table.head.map((text, i) => ({
                text,
                bold: true,
                ...(table.numeric.includes(i) ? { alignment: 'right' as const } : {}),
              })),
              ...table.rows.map((row) =>
                row.map((text, i) => ({
                  text,
                  ...(table.numeric.includes(i) ? { alignment: 'right' as const } : {}),
                })),
              ),
            ],
          },
          layout: CELL_LAYOUT,
          margin: [0, 0, 0, 8] as [number, number, number, number],
        },
      ]),
      ...(input.notes ?? []).map((text) => ({
        text,
        italics: true,
        margin: [0, 4, 0, 0] as [number, number, number, number],
      })),
    ],
  };
}

/** ROB Mutabakat Raporu (ekran 01 "ROB Mutabakat Raporu Oluştur") girdisi. */
export interface ReconciliationReportInput {
  vesselName: string;
  card: FuelOverviewReconciliation;
  sounding: FuelSounding | null;
  fuelCode: (id: string | null) => string;
  tankName: (id: string) => string;
  dateTime: (iso: string) => string;
  mt: (value: string | null) => string;
  printedAt: string;
  labels: {
    title: string;
    vessel: string;
    voyage: string;
    period: string;
    printed: string;
    product: string;
    opening: string;
    bunkered: string;
    consumed: string;
    calculated: string;
    measured: string;
    diff: string;
    sounding: string;
    tankTitle: string;
    noSounding: string;
    footer: string;
    tankColumns: readonly string[];
  };
}

export function reconciliationReportPdf(input: ReconciliationReportInput): TDocumentDefinitions {
  const { card, labels: l } = input;
  return tablePdf({
    title: l.title,
    info: [
      [l.vessel, input.vesselName],
      [l.voyage, card.voyageNo ?? '—'],
      [l.period, `${input.dateTime(card.from)} – ${input.dateTime(card.to)}`],
      [l.printed, input.printedAt],
    ],
    tables: [
      {
        head: [l.product, l.opening, l.bunkered, l.consumed, l.calculated, l.measured, l.diff],
        numeric: [1, 2, 3, 4, 5, 6],
        rows: card.fuels.map((f) => [
          input.fuelCode(f.fuelTypeId),
          input.mt(f.openingMt),
          input.mt(f.bunkeredMt),
          input.mt(f.consumedMt),
          input.mt(f.calculatedMt),
          input.mt(f.measuredMt),
          input.mt(f.diffMt),
        ]),
      },
      ...(input.sounding
        ? [
            {
              title: `${l.tankTitle} · ${l.sounding}`,
              head: [...l.tankColumns],
              numeric: [2, 3, 4, 5, 6],
              rows: input.sounding.lines.map((line) => [
                input.tankName(line.tankId),
                line.method,
                line.readingCm ?? '—',
                line.tempC ?? '—',
                line.observedVolumeM3 ?? '—',
                line.density15 ?? '—',
                input.mt(line.massMt),
              ]),
            },
          ]
        : []),
    ],
    footer: l.footer,
    notes: input.sounding ? [] : [l.noSounding],
  });
}
